import React, { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { FieldInvalidProvider } from '../../fieldInvalid'
import { InfoIcon } from '../../icons/line/InfoIcon'
import { FieldError } from '../field-error'
import { HStack } from '../hstack'
import { Icon } from '../icon'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { Tooltip, TooltipContent, TooltipText } from '../tooltip'
import { VStack } from '../vstack'

export type FormFieldProps = {
    label?: string
    required?: boolean
    /** Secondary note rendered next to the label, e.g. "(at least one is required)". */
    comment?: string
    tooltip?: string
    error?: string
    /** Explanatory text below the control. */
    hint?: ReactNode
    className?: string
    children: ReactNode
}

const REQUIRED_MARK = '*'
const TOOLTIP_ICON_SIZE = 20

/**
 * Label + control + error group shared by every form in the app.
 *
 * The label is `content/default` at the field type scale in regular weight and
 * sits 4px above the control; the required mark is part of the label string so
 * its spacing cannot drift with a translation's trailing whitespace.
 */
export const FormField: React.FC<FormFieldProps> = ({
    label,
    required,
    comment,
    tooltip,
    error,
    hint,
    className,
    children,
}) => (
    <VStack className={twMerge('gap-1', className)}>
        {label ? (
            <HStack className='w-full items-center gap-1 px-1'>
                <Text size='body' tone='default'>
                    {required ? `${label.trim()} ${REQUIRED_MARK}` : label}
                </Text>
                {comment ? (
                    <Text size='caption' tone='muted'>
                        {comment}
                    </Text>
                ) : null}
                {tooltip ? (
                    <Tooltip
                        placement='top'
                        trigger={triggerProps => (
                            <Pressable
                                {...triggerProps}
                                accessibilityRole='button'
                                className='ml-auto'
                            >
                                <Icon
                                    as={InfoIcon}
                                    size={TOOLTIP_ICON_SIZE}
                                    className='text-aos-text-muted'
                                />
                            </Pressable>
                        )}
                    >
                        <TooltipContent>
                            <TooltipText>{tooltip}</TooltipText>
                        </TooltipContent>
                    </Tooltip>
                ) : null}
            </HStack>
        ) : null}
        <FieldInvalidProvider isInvalid={!!error}>{children}</FieldInvalidProvider>
        {error ? <FieldError>{error}</FieldError> : null}
        {hint ? (
            <Text size='body' tone='muted'>
                {hint}
            </Text>
        ) : null}
    </VStack>
)

FormField.displayName = 'FormField'
